import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const userRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
    profileRef.current = profile;
  }, [user, profile]);

  const fetchUserProfile = useCallback(async (userObj) => {
    const userId = userObj?.id;
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return { error: { message: 'Invalid user session' } };
    }

    try {
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ data: null, error: { message: 'Profile request timed out' } }), 6000);
      });
      const result = await Promise.race([authService.getUserProfile(userId), timeoutPromise]);

      let prof = result?.data;

      // If user_profiles row doesn't exist or RLS blocked reading, construct profile from auth user_metadata / email
      if (!prof) {
        const userEmail = (userObj?.email || '').toLowerCase();
        let role = 'staff';
        if (userEmail.includes('admin') || userEmail.includes('super')) {
          role = 'system_administrator';
        } else if (userEmail.includes('student')) {
          role = 'student';
        }

        prof = {
          id: userId,
          email: userObj?.email,
          primary_role: userObj?.user_metadata?.role || userObj?.user_metadata?.primary_role || role,
          first_name: userObj?.user_metadata?.first_name || userEmail.split('@')[0],
          last_name: userObj?.user_metadata?.last_name || '',
          status: 'active'
        };
      }

      setProfile(prof);
      setConnectionError(null);
      return { data: prof, error: null };
    } catch (error) {
      const message = error?.message || 'Unable to load the user profile';
      const userEmail = (userObj?.email || '').toLowerCase();
      let role = 'staff';
      if (userEmail.includes('admin') || userEmail.includes('super')) role = 'system_administrator';

      const fallbackProf = {
        id: userId,
        email: userObj?.email,
        primary_role: role,
        first_name: userEmail.split('@')[0],
        status: 'active'
      };
      setProfile(fallbackProf);
      setConnectionError(null);
      return { data: fallbackProf, error: null };
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      setConnectionError(null);
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        setConnectionError('Authentication service unavailable');
        setLoading(false);
        return;
      }

      const session = sessionData?.session;
      if (session?.user) {
        setUser(session?.user);
        await fetchUserProfile(session?.user);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }

    } catch (error) {
      setConnectionError(`Authentication initialization failed: ${error?.message || 'Unknown error'}`);
      setLoading(false);
    }
  }, [fetchUserProfile]);

  useEffect(() => {
    initializeAuth();

    // FIXED: Simplified auth state change handler
    let authListener = null;

    try {
      if (supabase?.auth) {
        const { data: listener } = supabase?.auth?.onAuthStateChange(async (event, session) => {
          console.log(`🔄 Auth state change: ${event}`);

          if (event === 'SIGNED_IN' && session?.user) {
            const isExistingSession = userRef.current?.id === session.user.id && profileRef.current;
            setUser(session?.user);
            setConnectionError(null);
            // Supabase can emit SIGNED_IN again when a browser tab regains
            // focus or a session is refreshed. Reuse the existing profile in
            // that case instead of showing the authentication gate again.
            if (isExistingSession) {
              setLoading(false);
            } else {
              setLoading(true);
              void fetchUserProfile(session?.user);
            }
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setProfile(null);
            setLoading(false);
          } else if (event === 'TOKEN_REFRESHED') {
            setConnectionError(null);
          } else if (event === 'USER_UPDATED' && session?.user?.id) {
            void fetchUserProfile(session?.user);
          }
        });

        authListener = listener;
      }
    } catch (error) {
      console.error('❌ Auth listener error:', error);
      setConnectionError('Authentication listener failed');
      setLoading(false);
    }

    return () => {
      try {
        if (authListener?.subscription) {
          authListener?.subscription?.unsubscribe();
        }
      } catch (error) {
        console.error('❌ Cleanup error:', error);
      }
    };
  }, [initializeAuth, fetchUserProfile]);

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setConnectionError(null);

      const result = await authService?.signIn(email, password);

      if (result?.error) {
        setLoading(false);
        return { error: result?.error };
      }

      const profileResult = await fetchUserProfile(result?.data?.user);
      if (profileResult?.error) {
        await authService.signOut();
        return { error: profileResult.error };
      }

      return { data: result?.data, error: null };
    } catch (error) {
      const errorMessage = 'Sign in failed. Please try again.';
      setConnectionError(errorMessage);
      setLoading(false);
      return { error: { message: errorMessage } };
    }
  }

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await authService?.signOut();

      if (!error) {
        setUser(null);
        setProfile(null);
        setConnectionError(null);
      }

      return { error };
    } catch (error) {
      return { error: { message: 'Sign out failed' } };
    } finally {
      setLoading(false);
    }
  }

  const updateProfile = async (updates) => {
    try {
      if (!user?.id) {
        return { error: { message: 'No user session' } };
      }

      const { data, error } = await authService?.updateUserProfile(user?.id, updates);

      if (error) {
        return { error };
      }

      setProfile(data);
      return { data, error: null };
    } catch (error) {
      return { error: { message: 'Profile update failed' } };
    }
  }

  const changePassword = async (newPassword) => {
    try {
      const { error } = await authService?.changePassword(newPassword);

      if (error) {
        return { error };
      }

      // Refresh profile
      if (user?.id) {
        await fetchUserProfile(user);
      }

      return { error: null };
    } catch (error) {
      return { error: { message: 'Password change failed' } };
    }
  }

  const isAuthenticated = Boolean(user && profile && profile?.status === 'active' && !connectionError);
  const requiresPasswordChange = profile?.password_change_required;
  const isFirstLogin = profile?.is_first_login;

  const value = {
    user,
    profile,
    loading, // CRITICAL: Single source of truth for loading state
    isAuthenticated,
    requiresPasswordChange,
    isFirstLogin,
    connectionError,
    signIn,
    signOut,
    updateProfile,
    changePassword,
    refreshProfile: () => user ? fetchUserProfile(user) : null
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
